/**
 * Entry IIFE (`veo.js`, global `window.veo`). Igual que `index.ts` pero las
 * GUÍAS se cargan PEREZOSAMENTE (bundle `veo-guides.js`) — solo con
 * `guides:true` o al usar `veo.preview()`. Así `veo.js` queda chico (core +
 * spa-router + autocapture); los renderers + Floating UI van aparte.
 *
 * Los consumidores por bundler usan `@veo/sdk` (entry `index.ts`, estático).
 */
import { resolveApiUrl } from './core/api-url';
import { isBuilderMode } from './core/builder-loader';
import { loadGuides, type VeoGuidesApi } from './core/guides-loader';
import { getPreviewToken } from './core/preview-loader';
import { setupClient } from './core/setup';
import type { PreviewGuideInput, PreviewHandle, PreviewResult } from './plugins/guides';
import type { VeoClient, VeoConfig } from './types';

export { SDK_VERSION } from './contract/v1';
export { Client } from './core/client';
export { autocapturePlugin } from './plugins/autocapture';
export { definePlugin } from './plugins/plugin';
export { spaRouterPlugin } from './plugins/spa-router';
export type {
  VeoAutocaptureConfig,
  VeoClient,
  VeoConfig,
  VeoOrganization,
  VeoPlugin,
  VeoVisitor,
} from './types';

/** Inicializa el SDK (IIFE). Las guías se cargan perezosamente. */
export function init(config: VeoConfig): VeoClient {
  const apiUrl = resolveApiUrl(config);
  return setupClient(config, (client) => {
    const ensureGuides = (): Promise<VeoGuidesApi | null> =>
      loadGuides({
        ...(config.guidesUrl !== undefined ? { guidesUrl: config.guidesUrl } : {}),
        ...(config.debug !== undefined ? { debug: config.debug } : {}),
      });

    // Plugin runtime (guides:true): cargar el bundle e instalar al resolver.
    // En modo builder o preview-por-link NO se instala: ahí pinta la preview,
    // y el runtime duplicaría/taparía con las guías live.
    const previewToken = getPreviewToken();
    if (config.guides === true && !isBuilderMode() && previewToken === null) {
      void ensureGuides().then((guides) => {
        if (!guides) return;
        client.use(
          guides.guidesPlugin({
            apiUrl,
            apiKey: config.apiKey,
            ...(config.debug !== undefined ? { debug: config.debug } : {}),
          }),
        );
      });
    }

    // Modo preview-por-link (`?veoPreview=<token>`): cargar el bundle de guías
    // y renderizar la guía del token como vista previa sobre la app real.
    if (previewToken !== null) {
      void ensureGuides().then((guides) =>
        guides?.runPreviewMode({
          apiKey: config.apiKey,
          apiUrl,
          token: previewToken,
          ...(config.debug !== undefined ? { debug: config.debug } : {}),
        }),
      );
    }

    // `veo.preview()` / `veo.closePreview()` cargan las guías on-demand y
    // mantienen el shape { close, ready } aunque el bundle resuelva async.
    (client as unknown as Record<string, unknown>).preview = (
      input: PreviewGuideInput,
    ): PreviewHandle => {
      let inner: PreviewHandle | null = null;
      const ready: Promise<PreviewResult> = ensureGuides().then((guides) => {
        if (!guides) return { rendered: false };
        inner = guides.previewGuide(input);
        return inner.ready;
      });
      return { close: () => inner?.close(), ready, host: () => inner?.host() ?? null };
    };
    (client as unknown as Record<string, unknown>).closePreview = (): void => {
      void ensureGuides().then((guides) => guides?.closeGuidePreview());
    };
  });
}
